import { Injectable } from '@angular/core';

import { Activation, Fai, FaiBox, FaimUnlimited, PRICE_TYPES, ProductFactory } from '@bytel/bytel-sales';
import { CheckoutCartModel, CommercialSeries } from '@bytel/checkout';

import { MainCartService } from '@services/main-cart.service';

import { FaiRepository } from '@repositories/fai.repository';

import { IEligibility, IFaiAddressInstallation, IFaiBoxResult, IFaiData, IFaiVerticalStructure } from '@interfaces/fai.interface';

import { Observable, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CorbisCreateCartBody } from '../interfaces/api/corbis.interface';
import { ConfigurationService } from './configuration.service';

@Injectable({
    providedIn: 'root',
})
export class FaiService {
    public eligibility: IEligibility;
    public eligibility4g: IFaiBoxResult;
    public userAddress: IFaiAddressInstallation;
    public BIG_TRUST_CATEGORY = 'big-trust';

    public constructor(
        protected cartService: MainCartService,
        protected faiRepository: FaiRepository,
        protected configurationService: ConfigurationService,
    ) {
        this.init();
    }

    public addFai(gencode: string, isPro: boolean = false): Observable<CheckoutCartModel> {
        return this.cartService.addProducts([gencode], true).pipe(
            catchError((error) => {
                console.log('error', error);
                return error;
            }),
            map((cart: CheckoutCartModel) => {
                const faiProduct = this.cartService.getAllProductsByType<Fai>('Fai')[0];
                // Only use isPro when product is faim unlimited
                if (ProductFactory.Is(faiProduct, FaimUnlimited)) {
                    this.cartService.setPro(isPro);
                    this.cartService.setIsFromProWall(isPro);
                } else {
                    this.cartService.setPro((faiProduct as Fai).isPro);
                }

                const faiBox: FaiBox = this.cartService.getAllProductsByType<FaiBox>('FaiBox')[0];
                faiBox.technology = faiProduct?.data?.technology || null;

                this.cartService.save();
                return cart;
            }),
        );
    }

    public save(): void {
        this.faiRepository.save();
        this.init();
    }

    public init(): void {
        this.eligibility = this.faiRepository.getEligibility();
        this.eligibility4g = this.faiRepository.getEligibilityTokyo();
        this.userAddress = this.faiRepository.getEligAddress();
    }

    public getFaiTechnology(): string {
        const quote = this.cartService.getQuoteFai();
        const faiProduct = quote.getProductByType(Fai);
        return faiProduct?.data?.technology || null;
    }

    public isFmsToPayTodayByCategory(category: string): boolean {
        return category === this.BIG_TRUST_CATEGORY;
    }

    public isFmsToPayTodayByCommercialSerie(commercialSerie: CommercialSeries): boolean {
        return this.configurationService.getData()?.global?.commercialSeriesFmsToPayToday?.includes(commercialSerie);
    }

    public updateFmsPriceType(toPriceType: PRICE_TYPES): void {
        const quote = this.cartService.getQuoteFai();
        const activationProduct = quote.getProductByType(Activation);

        if (activationProduct) {
            activationProduct.priceType = toPriceType;
        }
    }

    public getVertical(): IFaiVerticalStructure {
        const buildings = this.faiRepository.getEligibility().buildings;
        return {
            building: buildings[0]?.building || '_NA_',
            stair: buildings[0]?.stairs[0]?.stair || '_NA_',
            floor: buildings[0]?.stairs[0]?.stages[0]?.floor || '_NA_',
            hasPto: buildings?.some((b) => b.stairs.some((s) => s.stages.some((stage) => stage.ptos.length >= 1))),
        };
    }

    public getFaiData(): IFaiData {
        return this.faiRepository.getFaiData();
    }

    /**
     * Check if current FAI offer in cart can subscribe to eligible option
     * @param offerInCart Current offer in cart
     * @returns boolean
     */
    public canSubscribeOptions(offerInCart: Fai): boolean {
        if (!offerInCart) {
            return false;
        }

        const faiData = this.getFaiData()
        const offers = faiData?.eligibility.offersWithOptions.find((offer) => offer.idOffer === offerInCart.gencode);

        if (!offers) {
            return false;
        }

        return (
            offers.options?.every((option) => offerInCart.upSells.includes(option)) ||
            offerInCart.upSells.includes('72156945')
        );
    }

    public async createCorbisCart(body: CorbisCreateCartBody): Promise<string> {
        const faiData = await lastValueFrom(this.faiRepository.createCorbisCart(body));
        return faiData?.idPanier || '';
    }
}
